import { useRef, useState } from "react";
import { UploadCloud, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Single-image uploader for documents (insurance card / registration photo).
export function DocUploader({ label, value, onChange, testid }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      <span className="label-caps text-slate-400 block mb-2">{label}</span>
      <div
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-4 flex items-center gap-3 transition-colors ${value ? "border-green-300 bg-green-50" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}
      >
        {value ? (
          <img src={value} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
        ) : (
          <span className="grid place-items-center w-14 h-14 rounded-lg bg-white text-blue-600 border border-slate-200"><UploadCloud size={22} /></span>
        )}
        <div className="min-w-0">
          {busy ? (
            <p className="text-sm text-slate-500 flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> {t("onboarding.uploadPhoto")}…</p>
          ) : value ? (
            <p className="text-sm text-green-700 font-semibold flex items-center gap-1.5"><CheckCircle2 size={15} /> {t("onboarding.uploaded")} · <span className="text-blue-600 underline">{t("onboarding.change")}</span></p>
          ) : (
            <>
              <p className="text-sm font-medium text-slate-700">{t("onboarding.uploadPhoto")}</p>
              <p className="text-xs text-slate-400">JPEG / PNG / WebP · up to 12 MB</p>
            </>
          )}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`${testid}-input`}
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}
